/**
 * Word Limit Configuration Component
 * 
 * Allows users to configure word limits for proposals:
 * - Total proposal limit
 * - Section-specific limits
 * - Page limits (with conversion to word count)
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Badge } from '@tracekit/core';
import { Settings, FileText, Hash } from 'lucide-react';
import { updateWordLimits } from '../services/rfpApi';
// Helper functions for page/word conversion
function estimateWordsFromPages(pages: number, wordsPerPage: number = 250): number {
  return pages * wordsPerPage;
}

function estimatePagesFromWords(words: number, wordsPerPage: number = 250): number {
  if (wordsPerPage <= 0) return 0;
  return words / wordsPerPage;
}

interface WordLimitConfigurationProps {
  reportId: string;
  currentWordLimit?: number;
  sectionLimits?: Record<string, number>;
  sections?: Array<{ id: string; section_name: string }>;
  onSave: (limits: {
    total_limit?: number;
    section_limits?: Record<string, number>;
  }) => Promise<void>;
}

export function WordLimitConfiguration({
  reportId,
  currentWordLimit,
  sectionLimits: currentSectionLimits,
  sections = [],
  onSave,
}: WordLimitConfigurationProps) {
  const [totalLimit, setTotalLimit] = useState<number | ''>(currentWordLimit || '');
  const [pageLimit, setPageLimit] = useState<number | ''>('');
  const [wordsPerPage, setWordsPerPage] = useState(250);
  const [sectionLimits, setSectionLimits] = useState<Record<string, number>>(
    currentSectionLimits || {}
  );
  const [isSaving, setIsSaving] = useState(false);
  
  // Update state when props change (e.g., when modal is reopened with fresh data)
  useEffect(() => {
    setTotalLimit(currentWordLimit || '');
    if (currentWordLimit && currentWordLimit > 0) {
      const pages = estimatePagesFromWords(currentWordLimit, wordsPerPage);
      setPageLimit(Math.round(pages * 10) / 10);
    } else {
      setPageLimit('');
    }
    setSectionLimits(currentSectionLimits || {});
  }, [currentWordLimit, currentSectionLimits, wordsPerPage]);
  
  // Convert page limit to word count
  const handlePageLimitChange = (pages: number | '') => {
    setPageLimit(pages);
    if (typeof pages === 'number' && pages > 0) {
      const wordCount = estimateWordsFromPages(pages, wordsPerPage);
      setTotalLimit(wordCount);
    }
  };
  
  // Convert word count to page estimate
  const handleTotalLimitChange = (words: number | '') => {
    setTotalLimit(words);
    if (typeof words === 'number' && words > 0) {
      const pages = estimatePagesFromWords(words, wordsPerPage);
      setPageLimit(Math.round(pages * 10) / 10); // Round to 1 decimal
    }
  };
  
  const handleSectionLimitChange = (sectionId: string, limit: number | '') => {
    setSectionLimits(prev => {
      const newLimits = { ...prev };
      if (typeof limit === 'number' && limit > 0) {
        newLimits[sectionId] = limit;
      } else {
        delete newLimits[sectionId];
      }
      return newLimits;
    });
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Map section limits from IDs to section names for API
      const sectionLimitsByName: Record<string, number> = {};
      if (Object.keys(sectionLimits).length > 0) {
        sections.forEach(section => {
          const sectionId = section.id || section.section_id;
          if (sectionId && sectionLimits[sectionId]) {
            sectionLimitsByName[section.section_name] = sectionLimits[sectionId];
          }
        });
      }
      
      // Update via API
      const response = await updateWordLimits(
        reportId,
        {
          total_limit: typeof totalLimit === 'number' ? totalLimit : undefined,
          section_limits: Object.keys(sectionLimitsByName).length > 0 ? sectionLimitsByName : undefined,
        }
      );
      
      // Also call onSave callback if provided (pass the response so parent can update cache)
      if (onSave) {
        await onSave({
          total_limit: response.word_limit,
          section_limits: response.section_limits,
        });
      }
    } catch (error: any) {
      console.error('Failed to save word limits:', error);
      alert(`Failed to save word limits: ${error.message || 'Unknown error'}`);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Card title="Word Limit Configuration">
      <div className="space-y-6">
        {/* Total Proposal Limit */}
        <div>
          <label className="block text-sm font-medium mb-2">Total Proposal Word Limit</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Word Count
              </label>
              <Input
                type="number"
                value={totalLimit}
                onChange={(e) => handleTotalLimitChange(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="Enter word limit"
                min={1}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Page Count (estimated)
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={pageLimit}
                  onChange={(e) => handlePageLimitChange(e.target.value ? parseFloat(e.target.value) : '')}
                  placeholder="Auto-calculated"
                  min={0.1}
                  step={0.1}
                  disabled
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={wordsPerPage}
                  onChange={(e) => setWordsPerPage(parseInt(e.target.value) || 250)}
                  placeholder="250"
                  min={100}
                  max={500}
                  className="w-24"
                  title="Words per page"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Words per page: {wordsPerPage} (adjustable)
              </p>
            </div>
          </div>
        </div>
        
        {/* Section-Specific Limits */}
        {sections.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Section-Specific Limits (Optional)
            </label>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Set individual word limits for specific sections. Leave empty to use proportional distribution.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              {sections.map(section => (
                <div key={section.id} className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {section.section_name}
                    </label>
                  </div>
                  <Input
                    type="number"
                    value={sectionLimits[section.id] || ''}
                    onChange={(e) => handleSectionLimitChange(
                      section.id,
                      e.target.value ? parseInt(e.target.value) : ''
                    )}
                    placeholder="No limit"
                    min={1}
                    className="w-32"
                  />
                  <span className="text-xs text-gray-500">words</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleSave}
            disabled={isSaving || (typeof totalLimit !== 'number' && Object.keys(sectionLimits).length === 0)}
          >
            {isSaving ? 'Saving...' : 'Save Word Limits'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
